import React, { createContext, useCallback, useContext, useMemo, useRef, useState } from "react";
import { Modal } from "./ui-modal";

// Fenêtre de confirmation aux couleurs d'Horizon — remplace window.confirm (décision du 28/09, lot 7)
// et window.prompt (décision du 30/09).
//   const { confirm, choose, ask } = useConfirm();
//   if (!(await confirm({ title, message?, confirmLabel?, cancelLabel?, tone? }))) return;   // tone : "danger" = bouton rouge
//   const v = await choose({ title, message?, actions: [{ label, value, tone? }] });         // null si la fenêtre est fermée
//   const s = await ask({ title, label, defaultValue?, placeholder?, confirmLabel?, select?, multiline? }); // texte saisi, ou null
//   multiline : zone de plusieurs lignes (Entrée passe à la ligne, Ctrl/Cmd + Entrée envoie) — consigne de correction (01/10).
// Fermer (croix, Échap, clic à côté) vaut toujours « ne rien faire ».
const Ctx = createContext(null);

// Corps de la fenêtre de saisie : un champ, Annuler, et le bouton d'action (Entrée le déclenche aussi, sauf en plusieurs lignes).
function AskBody({ req, onSubmit, onCancel }) {
  const [value, setValue] = useState(req.defaultValue || "");
  const empty = !value.trim();
  const submit = (e) => { e.preventDefault(); if (!empty) onSubmit(value.trim()); };
  const onFocus = (e) => {
    if (req.select) e.target.select();
    else { const n = e.target.value.length; e.target.setSelectionRange(n, n); }
  };
  return (
    <form onSubmit={submit} data-testid="prompt-dialog">
      {req.message && <p className="text-sm text-marine/80 whitespace-pre-line mb-4">{req.message}</p>}
      <label className="block text-xs font-semibold uppercase tracking-wider text-marine/80 mb-1.5" htmlFor="prompt-input">{req.label}</label>
      {req.multiline ? (
        <textarea id="prompt-input" data-autofocus data-testid="prompt-input" className="inp resize-y" rows={4} value={value} placeholder={req.placeholder}
          onChange={(e) => setValue(e.target.value)} onFocus={onFocus}
          onKeyDown={(e) => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) submit(e); }} />
      ) : (
        <input id="prompt-input" data-autofocus data-testid="prompt-input" className="inp" value={value} placeholder={req.placeholder}
          onChange={(e) => setValue(e.target.value)} onFocus={onFocus} autoComplete="off" />
      )}
      <div className="flex flex-wrap justify-end gap-2 mt-6">
        <button type="button" onClick={onCancel} data-testid="prompt-cancel" className={`rounded-sm px-4 py-2 text-sm font-medium transition-colors ${TONE.quiet}`}>{req.cancelLabel}</button>
        <button type="submit" disabled={empty} data-testid="prompt-submit" className={`rounded-sm px-4 py-2 text-sm font-medium transition-colors disabled:opacity-50 ${TONE.default}`}>{req.confirmLabel}</button>
      </div>
    </form>
  );
}

const TONE = {
  quiet: "border border-marine/25 text-marine hover:bg-marine-50",
  default: "bg-marine text-white hover:bg-marine-hover",
  danger: "bg-rouge text-white hover:bg-rouge-hover",
};

export function ConfirmProvider({ children }) {
  const [req, setReq] = useState(null);
  const reqRef = useRef(null);

  const settle = useCallback((value) => {
    const r = reqRef.current;
    reqRef.current = null;
    setReq(null);
    if (r) r.resolve(value);
  }, []);

  const choose = useCallback((opts) => new Promise((resolve) => {
    if (reqRef.current) reqRef.current.resolve(null); // une seule question à la fois
    const r = { ...opts, resolve, key: Date.now() };
    reqRef.current = r;
    setReq(r);
  }), []);

  const confirm = useCallback(async ({ confirmLabel = "Confirmer", cancelLabel = "Annuler", tone = "default", ...rest }) => {
    const v = await choose({ ...rest, actions: [{ label: cancelLabel, value: false, tone: "quiet" }, { label: confirmLabel, value: true, tone }] });
    return v === true;
  }, [choose]);

  const ask = useCallback(({ confirmLabel = "Enregistrer", cancelLabel = "Annuler", select = true, ...rest }) => new Promise((resolve) => {
    if (reqRef.current) reqRef.current.resolve(null);
    const r = { ...rest, kind: "ask", confirmLabel, cancelLabel, select, resolve, key: Date.now() };
    reqRef.current = r;
    setReq(r);
  }), []);

  const value = useMemo(() => ({ confirm, choose, ask }), [confirm, choose, ask]);

  return (
    <Ctx.Provider value={value}>
      {children}
      {req && (
        <Modal key={req.key} title={req.title} onClose={() => settle(null)} z="z-[90]">
          {req.kind === "ask" ? <AskBody req={req} onSubmit={(v) => settle(v)} onCancel={() => settle(null)} /> : (
          <div data-testid="confirm-dialog">
            {req.message && <p className="text-sm text-marine/80 whitespace-pre-line">{req.message}</p>}
            <div className={`flex flex-wrap justify-end gap-2 ${req.message ? "mt-6" : "mt-2"}`}>
              {req.actions.map((a, i) => (
                <button key={i} type="button" onClick={() => settle(a.value)} data-testid={`confirm-action-${i}`}
                  className={`rounded-sm px-4 py-2 text-sm font-medium transition-colors ${TONE[a.tone] || TONE.default}`}>
                  {a.label}
                </button>
              ))}
            </div>
          </div>
          )}
        </Modal>
      )}
    </Ctx.Provider>
  );
}

export function useConfirm() {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error("useConfirm : ConfirmProvider absent (il est posé dans App.js)");
  return ctx;
}
